<template>
    <!-- Campo com um seletor de tipo embutido à esquerda (ex.: R$ | % no desconto). O input vai no slot; o
         componente cuida só do tipo (v-model) e da pastilha. Parada, a pastilha mostra a sigla; o clique abre o
         menu, que é levado para o body e posicionado em fixed: fica por cima da tabela com rolagem e de qualquer card
         (mesma ideia do showOverflow do Autosuggest, sem depender do contêiner).
         Cache: a última escolha fica no localStorage por empresa, usuário e módulo (e `chave`), e é aplicada quando
         o registro ainda não tem valor (`aplicarCache`) — adicionar, e linha vazia em editar/clonar. -->
    <!-- formato="trilho": as opções ficam lado a lado, num trilho cinza colado à direita do campo, com a ativa em
         pastilha branca (para quando há espaço, como no modal). O cache vale igual nos dois formatos. -->
    <div v-if="formato == 'trilho'" class="b-form-input-seletor input-group flex-nowrap trilho">
        <slot />
        <div class="input-group-append">
            <div class="trilho-seletor" role="radiogroup" :aria-label="rotulo">
                <button
                    v-for="opcao in opcoes"
                    :key="opcao.valor"
                    type="button"
                    role="radio"
                    :aria-checked="opcao.valor == value ? 'true' : 'false'"
                    :title="opcao.rotulo"
                    class="opcao-trilho"
                    :class="{ ativa: opcao.valor == value }"
                    :disabled="disabled"
                    @click="escolhe(opcao.valor)"
                >
                    {{ opcao.sigla }}
                </button>
            </div>
        </div>
    </div>
    <div v-else class="b-form-input-seletor" :class="{ 'seletor-desabilitado': disabled }">
        <slot />
        <button
            ref="botao"
            type="button"
            class="pastilha-seletor"
            :class="{ aberta: aberto }"
            :disabled="disabled"
            :aria-expanded="aberto ? 'true' : 'false'"
            aria-haspopup="listbox"
            :aria-label="rotulo"
            @click="alterna"
            @keydown.down.prevent="abre"
            @keydown.esc="fecha"
        >
            {{ opcaoAtual ? opcaoAtual.sigla : value }}
        </button>
        <ul
            v-show="aberto"
            ref="menu"
            class="menu-seletor"
            role="listbox"
            :style="posicaoMenu"
        >
            <li
                v-for="opcao in opcoes"
                :key="opcao.valor"
                role="option"
                :aria-selected="opcao.valor == value ? 'true' : 'false'"
                :class="{ ativa: opcao.valor == value }"
                @mousedown.prevent="escolhe(opcao.valor)"
            >
                <span class="sigla-opcao">{{ opcao.sigla }}</span>
                {{ opcao.rotulo }}
            </li>
        </ul>
    </div>
</template>
<script>
export default {
    name: 'BFormInputSeletor',
    props: {
        // Tipo escolhido (v-model)
        value: {
            type: String,
            default: ''
        },
        // [{ valor, sigla, rotulo }]
        opcoes: {
            type: Array,
            default: () => [
                { valor: 'R$', sigla: 'R$', rotulo: 'Valor em reais' },
                { valor: '%', sigla: '%', rotulo: 'Porcentagem' }
            ]
        },
        disabled: {
            type: Boolean,
            default: false
        },
        // Nome do campo no cache (ex.: 'desconto_item'). Sem chave, não há cache.
        chave: {
            type: String,
            default: ''
        },
        // Módulo no cache; por padrão o primeiro segmento da URL (pedidos, estoque, financeiro...)
        modulo: {
            type: String,
            default: ''
        },
        // true quando o registro ainda não tem valor: o componente aplica a última escolha guardada
        aplicarCache: {
            type: Boolean,
            default: false
        },
        rotulo: {
            type: String,
            default: 'Tipo'
        },
        // 'pastilha' (sigla dentro do campo, menu ao clicar — tabelas) ou 'trilho' (opções lado a lado à direita)
        formato: {
            type: String,
            default: 'pastilha'
        }
    },
    data() {
        return {
            aberto: false,
            posicaoMenu: {}
        }
    },
    computed: {
        opcaoAtual() {
            return this.opcoes.find((opcao) => opcao.valor == this.value)
        },
        chaveCache() {
            if (!this.chave) {
                return ''
            }
            const usuario = this.$usuarioLogado || {}
            const empresa = (usuario.Empresa && usuario.Empresa.id) || ''
            const modulo = this.modulo || (window.location.pathname.split('/')[1] || 'core')
            return `seletor_${empresa}_${usuario.id || ''}_${modulo}_${this.chave}`
        }
    },
    mounted() {
        // O menu mora no body: dentro de .table-responsive (overflow) ele seria cortado
        if (this.$refs.menu) {
            document.body.appendChild(this.$refs.menu)
        }
        if (this.aplicarCache) {
            const guardado = this.leCache()
            if (guardado && guardado != this.value && this.opcoes.some((opcao) => opcao.valor == guardado)) {
                // Só o tipo: o registro não tem valor, então não há o que recalcular
                this.$emit('input', guardado)
            }
        }
    },
    beforeDestroy() {
        this.removeOuvintes()
        const menu = this.$refs.menu
        if (menu && menu.parentNode) {
            menu.parentNode.removeChild(menu)
        }
    },
    methods: {
        alterna() {
            this.aberto ? this.fecha() : this.abre()
        },
        abre() {
            if (this.disabled) {
                return
            }
            const caixa = this.$refs.botao.getBoundingClientRect()
            this.posicaoMenu = {
                top: `${caixa.bottom + 4}px`,
                left: `${caixa.left}px`
            }
            this.aberto = true
            document.addEventListener('mousedown', this.cliqueFora, true)
            window.addEventListener('scroll', this.fecha, true)
            window.addEventListener('resize', this.fecha)
        },
        fecha() {
            this.aberto = false
            this.removeOuvintes()
        },
        removeOuvintes() {
            document.removeEventListener('mousedown', this.cliqueFora, true)
            window.removeEventListener('scroll', this.fecha, true)
            window.removeEventListener('resize', this.fecha)
        },
        cliqueFora(evento) {
            if (this.$refs.menu.contains(evento.target) || this.$refs.botao.contains(evento.target)) {
                return
            }
            this.fecha()
        },
        escolhe(valor) {
            this.fecha()
            if (valor == this.value) {
                return
            }
            this.gravaCache(valor)
            this.$emit('input', valor)
            this.$emit('change', valor)
        },
        // localStorage pode faltar (aba anônima, bloqueio): o campo funciona igual, só não lembra
        leCache() {
            if (!this.chaveCache) {
                return ''
            }
            try {
                return window.localStorage.getItem(this.chaveCache) || ''
            } catch (e) {
                return ''
            }
        },
        gravaCache(valor) {
            if (!this.chaveCache) {
                return
            }
            try {
                window.localStorage.setItem(this.chaveCache, valor)
            } catch (e) {
                // sem cache
            }
        }
    }
}
</script>
<style scoped>
.b-form-input-seletor {
    position: relative;
}
/* O número continua à direita; o recuo abre espaço para a pastilha */
.b-form-input-seletor:not(.trilho) ::v-deep input {
    padding-left: 44px;
}
/* Trilho: cinza na moldura do input, a opção ativa como pastilha branca, sem cor */
.trilho-seletor {
    display: flex;
    align-items: center;
    gap: 2px;
    height: 100%;
    min-height: 38px;
    padding: 3px;
    border: 1px solid #ced4da;
    border-left: 0;
    border-radius: 0 var(--raio, 4px) var(--raio, 4px) 0;
    background: var(--cinza-superficie, #f4f5f7);
}
.opcao-trilho {
    min-width: 36px;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: var(--raio-sm, 4px);
    background: transparent;
    color: #6c757d;
    font-size: 13px;
    font-weight: 600;
    line-height: 30px;
    cursor: pointer;
}
.opcao-trilho:hover {
    color: #495057;
}
.opcao-trilho.ativa {
    background: var(--te-superficie, #fff);
    color: #495057;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}
.opcao-trilho:focus {
    outline: none;
}
.opcao-trilho:focus-visible {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.35);
}
.opcao-trilho:disabled {
    cursor: default;
}
.pastilha-seletor {
    position: absolute;
    top: 50%;
    left: 6px;
    transform: translateY(-50%);
    min-width: 30px;
    height: 24px;
    padding: 0 6px;
    border: 0;
    border-radius: var(--raio-sm, 4px);
    background: var(--cinza-superficie, #f4f5f7);
    color: #495057;
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
    cursor: pointer;
}
.pastilha-seletor:hover,
.pastilha-seletor.aberta {
    background: #e9ecef;
}
.pastilha-seletor:focus {
    outline: none;
}
.pastilha-seletor:focus-visible {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.35);
}
.pastilha-seletor:disabled {
    cursor: default;
    background: transparent;
    color: #adb5bd;
}
/* O menu é movido para o body: as regras não podem depender do pai */
.menu-seletor {
    position: fixed;
    z-index: 1060;
    min-width: 170px;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    font-size: 13px;
    background: var(--te-superficie, #fff);
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: var(--raio, 4px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.12);
}
.menu-seletor li {
    padding: 6px 12px;
    color: #495057;
    cursor: pointer;
    white-space: nowrap;
}
.menu-seletor li:hover {
    background: #f1f3f5;
}
.menu-seletor li.ativa {
    background: var(--cor-primaria, #16537e);
    color: #fff;
}
.sigla-opcao {
    display: inline-block;
    min-width: 22px;
    font-weight: 600;
}
</style>
